import React, { useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeProvider';
import { api } from '../utils/api';
import logoWhite from '../assets/logo-repro-white.svg';
import logoDark from '../assets/logo-repro-v3-dark.svg';

export default function ResetPassword() {
  const { resolvedTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const logo = resolvedTheme === 'dark' ? logoWhite : logoDark;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100 px-4">
        <div className="w-full max-w-sm text-center">
          <img src={logo} alt="RePro" className="h-8 mx-auto mb-4" />
          <div className="alert alert-error text-sm">
            Invalid or missing reset token. Please use the link from your email.
          </div>
          <Link to="/login" className="btn btn-ghost btn-sm mt-4">
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-100 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={logo} alt="RePro" className="h-8 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-base-content">Reset Password</h1>
          <p className="text-sm text-base-content/60 mt-1">Choose a new password</p>
        </div>

        <div className="card bg-base-200 shadow-md">
          <div className="card-body gap-4">
            {success ? (
              <div>
                <div className="alert alert-success text-sm">
                  Password has been reset successfully. You can now log in.
                </div>
                <Link to="/login" className="btn btn-primary btn-sm w-full mt-4">
                  Go to login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && <div className="alert alert-error text-sm mb-4">{error}</div>}

                <div className="form-control mb-4">
                  <label className="label" htmlFor="password">
                    <span className="label-text">New Password</span>
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    autoComplete="new-password"
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input input-bordered w-full"
                    minLength={8}
                  />
                  <label className="label">
                    <span className="label-text-alt text-base-content/50">
                      Min 8 chars, must include uppercase, lowercase, number, and special character
                    </span>
                  </label>
                </div>

                <div className="form-control mb-4">
                  <label className="label" htmlFor="confirmPassword">
                    <span className="label-text">Confirm Password</span>
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input input-bordered w-full"
                    minLength={8}
                  />
                </div>

                <button type="submit" disabled={loading} className="btn btn-primary w-full">
                  {loading ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    'Reset Password'
                  )}
                </button>

                <Link to="/login" className="btn btn-ghost btn-sm w-full mt-2">
                  Back to login
                </Link>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
